
import type { Role } from '@shared/user';
import { authPost } from '@client/shared/auth/index.ts';

export type SetPasswordOutcome =
  | { readonly kind: 'ok' }
  | { readonly kind: 'weak' }
  | { readonly kind: 'invalid-grant' }
  | { readonly kind: 'error'; readonly message: string };

export async function postSetPassword(input: {
  readonly grant: string;
  readonly password: string;
}): Promise<SetPasswordOutcome> {
  const response = await authPost('/auth/password', 'set-password', {
    grant: input.grant,
    password: input.password,
  });
  if (response.ok) return { kind: 'ok' };
  if (response.status === 422) return { kind: 'weak' };
  if (response.status === 401) return { kind: 'invalid-grant' };
  return { kind: 'error', message: await readError(response, 'Couldn’t set your password. Try again.') };
}


export interface CreatedOrganization {
  readonly id: string;
  readonly name: string;
}

export type CreateOrgOutcome =
  | { readonly kind: 'ok'; readonly organization: CreatedOrganization }
  | { readonly kind: 'error'; readonly code?: string; readonly message: string };

export async function postCreateOrganization(input: {
  readonly organizationName: string;
  readonly ownerName: string;
  readonly ownerRoles: readonly Role[];
}): Promise<CreateOrgOutcome> {
  const response = await authPost('/auth/create-organization', 'create-organization', {
    organizationName: input.organizationName,
    ownerName: input.ownerName,
    ownerRoles: input.ownerRoles,
  });

  const body = await readJson(response);
  if (response.ok && body && typeof body === 'object' && 'organization' in body) {
    const org = (body as { organization: CreatedOrganization }).organization;
    return { kind: 'ok', organization: org };
  }
  const code = body && typeof body === 'object' && 'error' in body ? String((body as { error: unknown }).error) : undefined;
  return { kind: 'error', code, message: createOrgMessage(code) };
}

function createOrgMessage(code: string | undefined): string {
  switch (code) {
    case 'ORGANIZATION_NAME_INVALID':
      return 'That group name isn’t valid: try another.';
    case 'OWNER_NAME_INVALID':
      return 'Your name isn’t valid: go back and fix it.';
    case 'OWNER_ROLES_INVALID':
      return 'Pick at least one role: owner is always included.';
    case 'EMAIL_ALREADY_OWNS_GROUP':
      return 'You already own a group. Sign in instead.';
    case 'ALREADY_IN_GROUP':
      return 'You’re already part of a group. Sign in instead.';
    default:
      return 'Couldn’t create your group. Try again.';
  }
}


async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function readError(response: Response, fallback: string): Promise<string> {
  const body = await readJson(response);
  if (body && typeof body === 'object') {
    const maybe = body as { error?: unknown; message?: unknown };
    if (typeof maybe.message === 'string') return maybe.message;
    if (typeof maybe.error === 'string') return maybe.error;
  }
  return fallback;
}
